import React, { useContext } from 'react';
import Select from '../../../../components/Select/Select';
import Button from '../../../../components/Button/Button';
import { IconArrowDown, IconClose } from '../../../../icons';
import { DocumentTypesEnum } from '../../../../core/enums/PublicationTypesEnum';
import { getSortTypes, sortOptions, SortTypesEnum } from '../../../../components/Filters/Filters.helpers';
import { CountriesEnum, GlobalContext } from '../../../../GlobalContext';

interface TagsBarProps {
    selectedTags: { id: string | number; title: string }[];
    handleRemoveTag: (id: string | number) => void;
    hasFilters: boolean;
    filters: {
        ribbonsIds: number[];
        categoriesIds: number[];
        subscriptionPackageIds: number[];
        tagsIds: number[];
        orderBy: string;
        sortOrder: string;
        hasAccess: boolean;
        types: string[];
        searchQuery?: string;
    };
    onChange: (next: TagsBarProps['filters']) => void;
    mode: 'tile' | 'table';
    shouldShowFilteredResults: boolean;
    selectedSortType: SortTypesEnum[];
    setSelectedSortType: React.Dispatch<React.SetStateAction<SortTypesEnum[]>>;
    TYPE_OPTIONS: { id: DocumentTypesEnum; title: string }[];
}

const TagsBar: React.FC<TagsBarProps> = ({
    selectedTags,
    handleRemoveTag,
    hasFilters,
    filters,
    onChange,
    mode,
    selectedSortType,
    setSelectedSortType,
    shouldShowFilteredResults,
    TYPE_OPTIONS
}) => {
    const { selectedCountryId } = useContext(GlobalContext);
    const isGlobal = selectedCountryId === CountriesEnum.Global;

    const isSortDropdownVisible = React.useMemo(() => mode !== 'table' && shouldShowFilteredResults, [filters, mode]);

    return (
        <div className="BrowseSection__container">
            <div className="BrowseSection__tags-section">
                <div className="BrowseSection__tags-container">
                    <div>
                        {selectedTags.length > 0 && (
                            <div className="BrowseSection__tags">
                                <span className="BrowseSection__tags-label">Tags:</span>
                                {selectedTags.map((tag) => (
                                    <Button
                                        key={tag.id}
                                        onClick={() => handleRemoveTag(tag.id)}
                                        className="BrowseSection__tag"
                                        unstyled
                                    >
                                        {tag.title}
                                        <IconClose className="BrowseSection__tag-icon" />
                                    </Button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="BrowseSection__right-section">
                        <div className="BrowseSection__right">
                            <div className="BrowseSection__type-filter">
                                <div className="BrowseSection__dropdown">
                                    {mode === 'tile' && !isGlobal && (
                                        <Select
                                            inputButton
                                            buttonContent={(isOpen: boolean) => (
                                                <span className="BrowseSection__button-label">
                                                    Type
                                                    <IconArrowDown
                                                        style={{
                                                            transform: isOpen ? 'rotate(180deg)' : undefined,
                                                            transition: 'transform 0.2s ease',
                                                            fontSize: '12px'
                                                        }}
                                                    />
                                                </span>
                                            )}
                                            allowUnselected={false}
                                            options={TYPE_OPTIONS}
                                            isSearchable={false}
                                            closeOnSelect
                                            preventResetOnSelected
                                            isMultiple={false}
                                            value={filters.types}
                                            onChange={(types) => onChange({ ...filters, types: types as DocumentTypesEnum[] })}
                                            dropdownPosition="right"
                                            showTickIcon={false}
                                            useBrowseStyles
                                            dropdownHeight={110}
                                        />
                                    )}
                                    {isSortDropdownVisible && (
                                        <div className="BrowseSection__dropdown-content">
                                            <Select
                                                title="Sort by:"
                                                options={sortOptions}
                                                value={selectedSortType}
                                                isLoading={false}
                                                onChange={(ids) => {
                                                    const sortType = ids[0] as SortTypesEnum;
                                                    if (sortType) {
                                                        setSelectedSortType([sortType]);
                                                        const { direction, sortBy } = getSortTypes(sortType);
                                                        onChange({
                                                            ...filters,
                                                            orderBy: sortBy,
                                                            sortOrder: direction
                                                        });
                                                    }
                                                }}
                                                isCheckboxHidden
                                                isSimpleInput
                                                dropdownPosition="right"
                                                dropdownHeight={160}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TagsBar;
